import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import RequestCategoryModal from '../components/RequestCategoryModal';
import './MyCategoryRequests.css';

function MyCategoryRequests() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRequests();
  }, [filter, page]);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `/api/category-requests/my-requests?status=${filter}&page=${page}&limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequests(response.data.requests);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching requests:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestSuccess = () => {
    setShowModal(false);
    setFilter('all');
    setPage(1);
    fetchRequests();
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { className: 'status-pending', text: 'Pending Review' },
      approved: { className: 'status-approved', text: 'Approved' },
      rejected: { className: 'status-rejected', text: 'Rejected' },
      withdrawn: { className: 'status-withdrawn', text: 'Withdrawn' }
    };
    const badge = badges[status] || badges.pending;
    return <span className={`status-badge ${badge.className}`}>{badge.text}</span>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="my-category-requests-page">
      <div className="page-header">
        <div>
          <h1>My Category Requests</h1>
          <p className="page-subtitle">
            Track your identity category requests and their status
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          + Request New Category
        </button>
      </div>

      <div className="filter-tabs">
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => { setFilter('all'); setPage(1); }}
        >
          All
        </button>
        <button
          className={filter === 'pending' ? 'active' : ''}
          onClick={() => { setFilter('pending'); setPage(1); }}
        >
          Pending
        </button>
        <button
          className={filter === 'approved' ? 'active' : ''}
          onClick={() => { setFilter('approved'); setPage(1); }}
        >
          Approved
        </button>
        <button
          className={filter === 'rejected' ? 'active' : ''}
          onClick={() => { setFilter('rejected'); setPage(1); }}
        >
          Rejected
        </button>
      </div>

      {isLoading ? (
        <div className="loading">Loading your requests...</div>
      ) : requests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h3>No requests yet</h3>
          <p>
            {filter === 'all'
              ? "You haven't submitted any category requests yet."
              : `You don't have any ${filter} requests.`}
          </p>
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            Submit Your First Request
          </button>
        </div>
      ) : (
        <>
          <div className="requests-list">
            {requests.map((request) => (
              <div key={request.request_id} className="request-card">
                <div className="request-header">
                  <h3>{request.category_name}</h3>
                  {getStatusBadge(request.status)}
                </div>

                <p className="request-description">{request.description}</p>

                <div className="request-reason">
                  <strong>Reason:</strong> {request.reason}
                </div>

                <div className="request-meta">
                  <span className="meta-item">
                    📅 Submitted: {formatDate(request.created_at)}
                  </span>
                  {request.upvote_count > 0 && (
                    <span className="meta-item">
                      👍 {request.upvote_count} {request.upvote_count === 1 ? 'upvote' : 'upvotes'}
                    </span>
                  )}
                </div>

                {request.status === 'approved' && (
                  <div className="approval-info">
                    <div className="success-message">
                      ✅ Approved by {request.reviewer_first_name} on {formatDate(request.reviewed_at)}
                    </div>
                    <p className="approval-note">
                      This category is now available for all members to use!
                    </p>
                  </div>
                )}

                {request.status === 'rejected' && request.rejection_reason && (
                  <div className="rejection-info">
                    <div className="rejection-header">
                      <strong>Rejection Reason:</strong>
                    </div>
                    <p className="rejection-reason">{request.rejection_reason}</p>
                    <p className="rejection-note">
                      Reviewed by {request.reviewer_first_name} on {formatDate(request.reviewed_at)}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="page-info">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      <RequestCategoryModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={handleRequestSuccess}
      />
    </div>
  );
}

export default MyCategoryRequests;
